import { useQuery } from '@tanstack/react-query';
import { Play } from 'lucide-react';

import { decideWatch, watchDestination } from './gate';
import { MembersLockChip } from './MembersLockChip';

import type { BrowseVideo } from '@/lib/queries/browse';

import { useAuth } from '@/lib/auth/useAuth';
import { freemiumQueries } from '@/lib/queries/freemium';

/**
 * A single 16:9 video thumbnail in a category row. The freemium gate (PLAN §4):
 * the card is unmarked until a free member is out of plays and this title isn't
 * already unlocked this month — then the silver Members lock chip shows and
 * Watch routes to upgrade. Anon → sign-in. Fail-open while status loads (the
 * server RPC is the authoritative gate). Watch is a plain anchor; the player
 * route lands in its own unit.
 */
export function VideoCard({
  video,
  progressPercent,
}: {
  video: BrowseVideo;
  /** Spec 34: 0–100 resume progress. Draws a thin bar at the thumbnail's bottom
   *  edge (continue-watching rail only). Absent → the card is byte-identical. */
  progressPercent?: number;
}) {
  const { session } = useAuth();
  const signedIn = Boolean(session);
  const { data: status } = useQuery(freemiumQueries.status(signedIn));

  const decision =
    signedIn && !status
      ? 'play' // fail-open while status loads/errors
      : decideWatch({
          kind: 'vod',
          signedIn,
          isMember: status?.isMember ?? false,
          unlocked: status?.unlockedIds.includes(video.id) ?? false,
          remaining: status?.remaining ?? 0,
        });
  const locked = decision === 'upgrade';

  return (
    <a href={watchDestination(decision, video.watchHref)} className="group block">
      <div className="relative aspect-video overflow-hidden rounded-md bg-video-card ring-1 ring-video-line">
        {video.thumbnailUrl ? (
          <img
            src={video.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover transition group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-video-text-faint">
            <Play className="h-7 w-7" />
          </div>
        )}
        {locked && (
          <div className="absolute right-2 top-2">
            <MembersLockChip />
          </div>
        )}
        {typeof progressPercent === 'number' && progressPercent > 0 && (
          <div className="absolute inset-x-0 bottom-0 h-[3px] bg-video-line/60">
            <div
              className="h-full bg-oxblood-live"
              style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
            />
          </div>
        )}
      </div>
      <p className="mt-2 truncate font-sans text-sm text-video-text">{video.title}</p>
      <p className="truncate font-sans text-xs text-video-text-muted">
        {video.creatorName}
        {video.durationLabel ? ` · ${video.durationLabel}` : ''}
      </p>
    </a>
  );
}
